import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { loadActiveTenant, storeActiveTenant } from '../lib/tenantStorage';
import localDatabase from '../services/localDatabase';

type TenantRecord = {
  id: string;
  name: string;
  slug?: string | null;
  role?: string | null;
};

interface TenantContextValue {
  tenantId: string | null;
  tenants: TenantRecord[];
  loading: boolean;
  dbReady: boolean;
  setActiveTenantId: (tenantId: string) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export const useTenant = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider');
  return ctx;
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dbReady, setDbReady] = useState<boolean>(false);

  const setActiveTenantId = (id: string) => {
    const match = tenants.find((t) => t.id === id);
    if (!match) return;
    setTenantId(id);
    if (user?.id) {
      storeActiveTenant(user.id, id);
    }
  };

  const refreshTenants = useCallback(async () => {
    if (!user) {
      setTenants([]);
      setTenantId(null);
      return;
    }
    setLoading(true);
    // Don't set dbReady here - let the applyTenant effect handle it
    // Setting dbReady here causes it to get stuck if tenantId doesn't change
    const { data, error } = await supabase
      .from('tenant_users')
      .select('tenant_id, role, tenants:tenant_id ( id, name, slug )')
      .eq('user_id', user.id);

    if (error) {
      console.error('Failed to load tenant memberships', error.message);
      setTenants([]);
      setTenantId(null);
      setLoading(false);
      return;
    }

    const mapped: TenantRecord[] =
      data?.map((row: any) => ({
        id: row.tenants?.id ?? row.tenant_id,
        name: row.tenants?.name ?? 'Unknown tenant',
        slug: row.tenants?.slug ?? null,
        role: row.role ?? null,
      })) ?? [];

    setTenants(mapped);

    // Restore last selection or auto-pick single tenant
    if (user.id) {
      const stored = loadActiveTenant(user.id);
      if (stored && mapped.some((t) => t.id === stored)) {
        setTenantId(stored);
      } else if (mapped.length === 1) {
        setTenantId(mapped[0].id);
        storeActiveTenant(user.id, mapped[0].id);
      } else {
        setTenantId(null);
      }
    }

    setLoading(false);
    // Don't set dbReady here - let the applyTenant effect handle it
  }, [user]);

  useEffect(() => {
    // When user changes, refresh memberships
    refreshTenants();
    if (!user?.id) {
      setTenants([]);
      setTenantId(null);
    }
  }, [user, refreshTenants]);

  useEffect(() => {
    const applyTenant = async () => {
      setDbReady(false);
      try {
        await localDatabase.setTenantContext(tenantId || null);
        setDbReady(true);
      } catch (err) {
        console.error('Failed to switch local DB tenant context', err);
        setDbReady(false);
      }
    };
    applyTenant();
  }, [tenantId]);

  const value: TenantContextValue = {
    tenantId,
    tenants,
    loading,
    dbReady,
    setActiveTenantId,
    refreshTenants,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

